import { ApolloClient } from '@apollo/client';
import { notNil } from '@sixfold/typed-primitives';

import { companyGroupPartnersQuery, companyGroupRelationshipsQuery } from './graphql';
import {
  CompanyGroupPartnersQuery,
  CompanyGroupPartnersQueryVariables,
  CompanyGroupRelationshipsQuery,
  CompanyGroupRelationshipsQueryVariables,
  CompanyRelationshipType,
  getNodes,
} from '../lib/graphql';

export type CompanyGroupRelationship = NonNullable<
  NonNullable<NonNullable<NonNullable<CompanyGroupRelationshipsQuery['companyRelationships']>['edges']>[number]>['node']
>;

const PAGE_SIZE = 500;

export type CompanyGroupRole = 'none' | 'hub' | 'member';

type RelationshipFilter = Pick<CompanyGroupRelationshipsQueryVariables, 'sourceCompanyId' | 'targetCompanyId'>;

export async function fetchCompanyGroupRelationships(
  client: ApolloClient<object>,
  filter: RelationshipFilter,
): Promise<CompanyGroupRelationship[]> {
  const relationships: CompanyGroupRelationship[] = [];
  let cursor: string | undefined;

  do {
    const { data } = await client.query<CompanyGroupRelationshipsQuery, CompanyGroupRelationshipsQueryVariables>({
      query: companyGroupRelationshipsQuery,
      variables: { ...filter, limit: PAGE_SIZE, cursor },
      fetchPolicy: 'network-only',
    });
    const connection = data.companyRelationships;
    relationships.push(...getNodes(connection));
    const endCursor = connection?.pageInfo.endCursor ?? undefined;
    cursor = connection?.pageInfo.hasNextPage === true && notNil(endCursor) ? endCursor : undefined;
  } while (notNil(cursor));

  return relationships;
}

export type CompanyGroupPartnerRelationship = NonNullable<
  NonNullable<NonNullable<NonNullable<CompanyGroupPartnersQuery['companyRelationships']>['edges']>[number]>['node']
>;

const PARTNER_RELATIONSHIP_TYPES = [CompanyRelationshipType.CARRIER, CompanyRelationshipType.SUBCARRIER];

async function fetchPartnerRelationships(
  client: ApolloClient<object>,
  variables: Pick<CompanyGroupPartnersQueryVariables, 'targetCompanyId' | 'relationshipType'>,
): Promise<CompanyGroupPartnerRelationship[]> {
  const relationships: CompanyGroupPartnerRelationship[] = [];
  let cursor: string | undefined;

  do {
    const { data } = await client.query<CompanyGroupPartnersQuery, CompanyGroupPartnersQueryVariables>({
      query: companyGroupPartnersQuery,
      variables: { ...variables, limit: PAGE_SIZE, cursor },
      fetchPolicy: 'network-only',
    });
    const connection = data.companyRelationships;
    relationships.push(...getNodes(connection));
    const endCursor = connection?.pageInfo.endCursor ?? undefined;
    cursor = connection?.pageInfo.hasNextPage === true && notNil(endCursor) ? endCursor : undefined;
  } while (notNil(cursor));

  return relationships;
}

export async function fetchCompanyGroupPartnerRelationships(
  client: ApolloClient<object>,
  memberIds: string[],
): Promise<CompanyGroupPartnerRelationship[]> {
  const members = new Set(memberIds);
  const relationships = await Promise.all(
    memberIds.flatMap((targetCompanyId) =>
      PARTNER_RELATIONSHIP_TYPES.map((relationshipType) =>
        fetchPartnerRelationships(client, { targetCompanyId, relationshipType }),
      ),
    ),
  );

  return relationships.flat().filter(({ sourceCompany }) => !members.has(sourceCompany.company_id));
}

export async function fetchCompanyGroupRole(
  client: ApolloClient<object>,
  companyId: string,
): Promise<CompanyGroupRole> {
  const [asMember, asHub] = await Promise.all([
    fetchCompanyGroupRelationships(client, { sourceCompanyId: companyId }),
    fetchCompanyGroupRelationships(client, { targetCompanyId: companyId }),
  ]);

  if (asMember.length > 0) {
    return 'member';
  }
  return asHub.length > 0 ? 'hub' : 'none';
}
